import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import type { TreatmentReportFormData } from '@/schemas/treatmentReportSchema';
import type { ExistingTreatmentReport } from '../types';

interface UseTreatmentReportSecondFlagParams {
  control: Control<TreatmentReportFormData>;
  existingReports: ExistingTreatmentReport[];
  currentReportId?: number;
}

export const useTreatmentReportSecondFlag = ({
  control,
  existingReports,
  currentReportId,
}: UseTreatmentReportSecondFlagParams) => {
  const watchedYm = useWatch({ control, name: 'treatment_yearmonth' });
  const watchedDefault = useWatch({ control, name: 'is_treatment_report_default' });

  return useMemo(() => {
    if (!watchedYm) return false;
    const others = existingReports.filter(r => r.id !== currentReportId);
    const sameYm = others.filter(r => r.treatment_yearmonth === watchedYm);

    if (sameYm.length >= 1) return true;

    // 5-month gap check — only when 施術報告書交付料を算定しない is unchecked (= 算定する)
    if (!watchedDefault) {
      const sorted = others
        .filter(r => r.treatment_yearmonth && r.treatment_yearmonth < watchedYm)
        .sort((a, b) => b.treatment_yearmonth - a.treatment_yearmonth);
      const prev = sorted[0];

      if (prev) {
        const prevYm = prev.treatment_yearmonth;
        const monthDiff =
          (Math.floor(watchedYm / 100) - Math.floor(prevYm / 100)) * 12 +
          (watchedYm % 100) -
          (prevYm % 100);
        if (monthDiff < 5) return true;
      }
    }

    return false;
  }, [watchedYm, watchedDefault, existingReports, currentReportId]);
};

